import useSWR, { type KeyedMutator } from "swr";
// @ts-expect-error pure js
import useAuthenticatedAxios from "./use-authenticated-axios";
// @ts-expect-error pure js
import { findAndUpdate } from "../utils";
import { useMemo } from "react";
// @ts-expect-error pure js
import useGroupBy from "./use-group-by";
// @ts-expect-error pure js
import useAuth from "./use-auth.js";
import { type AxiosInstance } from "axios";

interface Resource {
  id: number;
  position?: number;
}

interface RequestOptions {
  params?: Record<string, unknown>;
}

interface ReturnOptions {
  withById?: boolean;
  groupBy?: {
    attr?: string | null;
    unique?: boolean;
  };
}

interface MutationOptions<T extends Resource = Resource> {
  alternateRootPath?: string;
  sort?: (a: T, b: T) => number;
  shouldMutate?: boolean;
  alternatePath?: string;
}

interface UseResourcesReturn<T extends Resource> {
  resources: T[];
  resourcesById: Record<number, T> | Record<string, T[]>;
  groupedResources: Record<number, T> | Record<string, T[]>;
  error: Error | undefined;
  isLoading: boolean;
  create: (resource: Partial<T>, options?: MutationOptions<T>) => Promise<T>;
  batchCreate: (
    resources: Partial<T>[],
    options?: MutationOptions<T>,
  ) => Promise<T[]>;
  batchUpdate: (
    resources: Partial<T>[],
    options?: MutationOptions<T> & { deletedIds?: number[] },
  ) => Promise<T[]>;
  duplicate: (resourceId: number, options?: MutationOptions<T>) => Promise<T>;
  update: (
    resourceId: number,
    resource: Partial<T>,
    options?: MutationOptions<T>,
  ) => Promise<T>;
  reorder: (resourceIds: number[]) => Promise<T[] | undefined>;
  del: (
    resourceId: number,
    options?: MutationOptions<T>,
  ) => Promise<T[] | undefined>;
  mutate: KeyedMutator<T[]>;
  axios: AxiosInstance;
}

export default function useResources<T extends Resource = Resource>(
  rootPath: string | null,
  requestOptions: RequestOptions = {},
  returnOptions: ReturnOptions = {},
): UseResourcesReturn<T> {
  const {
    withById = false,
    groupBy: { attr: groupByAttr = null, unique: groupByUnique = false } = {},
  } = returnOptions;
  const axios = useAuthenticatedAxios();
  const fallbackData = useMemo<T[]>(() => [], []);
  const { auth } = useAuth();
  const {
    data: resources,
    error,
    isLoading,
    mutate,
  } = useSWR<T[]>(
    rootPath
      ? { url: rootPath, options: requestOptions, userId: auth.userId }
      : null,
    ({ url, options }: { url: string; options: RequestOptions }) => {
      return axios
        .get(url, { params: options.params })
        .then((res: { data: T[] }) => res.data);
    },
    { fallbackData },
  );

  async function create(
    resource: Partial<T>,
    options: MutationOptions<T> = {},
  ): Promise<T> {
    let newResource: T;
    const { alternateRootPath, sort, shouldMutate = true } = options;
    async function makeRequest(): Promise<T> {
      const response = await axios.post(
        alternateRootPath ?? rootPath,
        resource,
      );
      return response.data;
    }

    if (shouldMutate) {
      await mutate(
        async (current: T[] | undefined) => {
          newResource = await makeRequest();
          const newResources = [...(current || []), newResource];
          if (sort) {
            newResources.sort(sort);
          }
          return newResources;
        },
        { revalidate: false },
      );
    } else {
      newResource = await makeRequest();
    }

    return newResource!;
  }

  async function batchCreate(
    resourcesToCreate: Partial<T>[],
    options: MutationOptions<T> = {},
  ): Promise<T[]> {
    let newResources: T[];
    const { alternateRootPath, sort } = options;
    await mutate(
      async (current: T[] | undefined) => {
        const response = await axios.post(
          `${alternateRootPath ?? rootPath}/batch-create`,
          resourcesToCreate,
        );
        newResources = response.data;
        const allResources = [...(current || []), ...newResources];
        if (sort) {
          allResources.sort(sort);
        }
        return allResources;
      },
      { revalidate: false },
    );
    return newResources!;
  }

  async function batchUpdate(
    resourcesToUpdate: Partial<T>[],
    options: MutationOptions<T> & { deletedIds?: number[] } = {},
  ): Promise<T[]> {
    let updatedResources: T[];
    const { alternateRootPath, sort, deletedIds = [] } = options;
    await mutate(
      async (current: T[] | undefined) => {
        const response = await axios.put(
          `${alternateRootPath ?? rootPath}/batch-update`,
          { resources: resourcesToUpdate, deleted_ids: deletedIds },
        );
        updatedResources = response.data;
        const updatedById = new Map(
          updatedResources.map((r) => [r.id, r] as const),
        );
        const deletedSet = new Set(deletedIds);
        const merged = (current || [])
          .filter((r) => !deletedSet.has(r.id))
          .map((r) => updatedById.get(r.id) ?? r);
        const mergedIds = new Set(merged.map((r) => r.id));
        for (const r of updatedResources) {
          if (!mergedIds.has(r.id)) {
            merged.push(r);
          }
        }
        if (sort) {
          merged.sort(sort);
        }
        return merged;
      },
      { revalidate: false },
    );
    return updatedResources!;
  }

  async function duplicate(
    resourceId: number,
    options: MutationOptions<T> = {},
  ): Promise<T> {
    let newResource: T;
    const { alternateRootPath, sort } = options;
    await mutate(
      async (current: T[] | undefined) => {
        const response = await axios.post(
          `${alternateRootPath ?? rootPath}/${resourceId}/duplicate`,
        );
        newResource = response.data;
        const newResources = [...(current || []), newResource];
        if (sort) {
          newResources.sort(sort);
        }
        return newResources;
      },
      { revalidate: false },
    );
    return newResource!;
  }

  function del(resourceId: number, options: MutationOptions<T> = {}) {
    const { alternateRootPath } = options;
    return mutate(
      async (current: T[] | undefined) => {
        await axios.delete(`${alternateRootPath ?? rootPath}/${resourceId}`);
        return (current || []).filter((resource) => resource.id !== resourceId);
      },
      { revalidate: false },
    );
  }

  async function update(
    resourceId: number,
    resource: Partial<T>,
    options: MutationOptions<T> = {},
  ): Promise<T> {
    const {
      alternateRootPath,
      sort,
      alternatePath,
      shouldMutate = true,
    } = options;
    let newResource: T;
    async function makeRequest(): Promise<T> {
      const response = await axios.put(
        `${alternateRootPath ?? rootPath}/${resourceId}${alternatePath ?? ""}`,
        resource,
      );
      return response.data;
    }
    if (shouldMutate) {
      await mutate(
        async (current: T[] | undefined) => {
          newResource = await makeRequest();
          const newResources = findAndUpdate(
            current || [],
            (r: T) => r.id === resourceId,
            newResource,
          );
          if (sort) {
            newResources.sort(sort);
          }
          return newResources;
        },
        { revalidate: false },
      );
    } else {
      newResource = await makeRequest();
    }
    return newResource!;
  }

  async function reorder(resourceIds: number[]) {
    return mutate(
      async () => {
        const res = await axios.put(`${rootPath}/order`, resourceIds);
        return res.data;
      },
      {
        optimisticData: resourceIds.map((id) =>
          (resources || []).find((r) => r.id === id),
        ) as T[],
        rollbackOnError: () => true,
        revalidate: false,
      },
    );
  }

  const groupBy = {
    attr: withById ? "id" : groupByAttr,
    unique: withById ? true : groupByUnique,
  };
  const resourcesById = useGroupBy(resources, groupBy);

  return {
    resources: resources || [],
    resourcesById,
    groupedResources: resourcesById,
    error,
    isLoading,
    create,
    batchCreate,
    batchUpdate,
    duplicate,
    update,
    reorder,
    del,
    mutate,
    axios,
  };
}
